import type { Reducer } from 'redux';
import { normalize } from 'normalizr';
import equal from 'deep-equal';

import {
  ADD_ENTITIES,
  REMOVE_ENTITIES,
  type EntitiesAction,
} from '../actions';
import schemas from '../util/schemas';
import parse from '../util/parser';
import type { EntityType, EntityTypeMap } from '../util/entityTypes';

export type EntitiesState = {
  [K in EntityType]?: Record<string, EntityTypeMap[K]>;
} & {
  [key: string]: Record<string, any> | undefined;
};

interface ReducerResult {
  state: EntitiesState;
  result?: any;
}

const initialState: EntitiesState = {};

function mergeUnique(arr1: any[] | undefined, arr2: any[]): any[] {
  const arr = arr1 ? [...arr1] : [];
  arr2.forEach((e) => {
    if (!arr1 || !arr1.includes(e)) {
      arr.push(e);
    }
  });
  return arr;
}

function addEntity(state: EntitiesState, type: string, data: any): ReducerResult {
  if (!type) {
    return { state, result: data.result };
  }
  const newState: EntitiesState = { ...state };
  const newData = normalize(data, schemas.getSchema(type));
  Object.keys(newData.entities).forEach((key) => {
    newState[key] = { ...newState[key], ...newData.entities[key] };
  });
  return { state: newState, result: newData.result };
}

function addEntities(state: EntitiesState, type: string, data: any): ReducerResult {
  if (!type) {
    return { state, result: data.result };
  }
  const newState: EntitiesState = { ...state };
  const newData = normalize(data, [schemas.getSchema(type)]);
  Object.keys(newData.entities).forEach((key) => {
    newState[key] = { ...newState[key], ...newData.entities[key] };
  });
  return { state: newState, result: newData.result };
}

function removeEntities(
  state: EntitiesState,
  type: string,
  ids: string | string[] = [],
): ReducerResult {
  if (!type) {
    return { state, result: [] };
  }
  let newIds: string[];
  if (typeof ids === 'string') {
    newIds = [ids];
  } else {
    newIds = ids;
  }
  let newState: EntitiesState = { ...state };
  newIds.forEach((id) => {
    const newData = removeEntity(newState, type, id);
    newState = newData.state;
  });
  newState[type] = { ...newState[type] };
  return { state: newState, result: [] };
}

function removeAllEntities(state: EntitiesState, type: string): ReducerResult {
  if (!type) {
    return { state, result: [] };
  }
  let newState: EntitiesState = { ...state };
  const entities = newState[type];
  if (entities) {
    const newData = removeEntities(newState, type, Object.keys(entities));
    newState = newData.state;
  }
  return { state: newState, result: [] };
}

function addChildEntities(
  state: EntitiesState,
  type: string,
  id: string,
  child: string,
  data: any,
  reset = true,
): ReducerResult {
  if (!type) {
    return { state, result: data.result };
  }
  let newData: ReducerResult;
  let result: any;
  let newState: EntitiesState = { ...state };
  const def = schemas.getSchemaTree(type);
  const element = newState[type] && newState[type][id];
  let childDef = def.dependencies.find((d) => d.key === child);
  if (!childDef) {
    childDef = {
      key: child,
      type: data.constructor === Array ? 'many' : 'one',
    };
  }
  if (childDef.type === 'many') {
    newData = addEntities(newState, child, data);
    result = newData.result;
    if (
      element &&
      !equal(
        element[child],
        data.map(i => i.meta.id),
      )
    ) {
      const newElement = { ...element };
      newElement[child] = reset ? result : mergeUnique(element[child], result);
      newState[type][id] = newElement;
    }
  } else {
    newData = addEntity(newState, child, data);
    result = newData.result;
    if (element && !equal(element[child], data.meta.id)) {
      const newElement = { ...element };
      newElement[child] = result;
      newState[type][id] = newElement;
    }
  }
  newState = newData.state;
  return { state: newState, result };
}

function removeEntity(state: EntitiesState, type: string, id: string): ReducerResult {
  if (!type) {
    return { state };
  }
  let newState: EntitiesState = { ...state };
  const def = schemas.getSchemaTree(type);
  const element = newState[type] && newState[type][id];
  if (element) {
    def.dependencies.forEach((dep) => {
      const newData = removeChildEntities(newState, type, id, dep.key);
      newState = newData.state;
    });
    delete newState[type][id];
  }
  return { state: newState };
}

function removeChildEntities(
  state: EntitiesState,
  type: string,
  id: string,
  child: string,
  ids?: string[],
): ReducerResult {
  if (!type) {
    return { state, result: undefined };
  }
  let result: any;
  const def = schemas.getSchemaTree(type);
  const element = state[type] && state[type][id];
  const childDef = def.dependencies.find((d) => d.key === child);
  const newData = removeEntities(state, child, ids || (element && element[child]) || []);
  const newState = newData.state;
  if (
    (childDef && childDef.type === 'many') ||
    (element && element[child] && element[child].constructor === Array)
  ) {
    if (ids && element) {
      result = element[child].filter((c: any) => !ids.includes(c));
    } else {
      result = [];
    }
  } else {
    result = undefined;
  }
  if (element) {
    const newElement = { ...element };
    newElement[child] = result;
    newState[type][id] = newElement;
  }
  return { state: newState, result };
}

const reducer: Reducer<EntitiesState, EntitiesAction> = (state = initialState, action) => {
  let newData: ReducerResult;
  let data: any;
  let newState: EntitiesState = { ...state };

  switch (action.type) {
    case ADD_ENTITIES:
      data = parse(action.data);
      if (data.constructor === Object) {
        data = [data];
      }
      if (action.options.parent) {
        if (action.options.reset !== false) {
          newData = removeChildEntities(
            newState,
            action.options.parent.type,
            action.options.parent.id,
            action.service,
          );
          newState = newData.state;
        }
        newData = addChildEntities(
          newState,
          action.options.parent.type,
          action.options.parent.id,
          action.service,
          data,
          action.options.reset,
        );
        newState = newData.state;
      } else {
        if (action.options.reset !== false) {
          newData = removeAllEntities(newState, action.service);
          newState = newData.state;
        }
        newData = addEntities(newState, action.service, data);
        newState = newData.state;
      }
      return newState;
    case REMOVE_ENTITIES: {
      let ids = action.ids;
      if (action.options.parent) {
        newData = removeChildEntities(
          newState,
          action.options.parent.type,
          action.options.parent.id,
          action.service,
          Array.isArray(ids) ? ids : ids ? [ids] : undefined,
        );
        newState = newData.state;
      } else {
        if (!ids) {
          ids = Object.keys(newState[action.service] || {});
        }
        newData = removeEntities(newState, action.service, ids);
        newState = newData.state;
      }
      return newState;
    }
    default:
      return state;
  }
};

export default reducer;
